import React, { useCallback, useEffect, useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import type { MultiValue } from "react-select";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import EmailListInput, { type Contact } from "~/common/EmailListInput";
import UserMultiSelector, {
	type MultiValueType,
	type UserMultiSelectorOption,
} from "~/common/UserSelect/UserMultiSelector";
import recaptcha from "~/lib/recaptcha";
import { getFirstUserFacingError } from "~/lib/utils";
import { environment } from "~/models";
import { Button, toast } from "~/ui";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import type { ShareModalQuery$data } from "~/__generated__/ShareModalQuery.graphql";
import type { ShareSectionMutation as ShareMutationType } from "~/__generated__/ShareSectionMutation.graphql";
import type { stream_mainstream$data as MainStream } from "~/__generated__/stream_mainstream.graphql";

import styles from "../css/ShareModal.module.css";

const shareStreamMutation = graphql`
	mutation ShareSectionMutation($id: ID!, $emails: [String!]!, $captchaToken: String!) {
		noteStreamShare(id: $id, emails: $emails, captchaToken: $captchaToken) {
			noteStream {
				id
				index
				userCount
				notestreamusers {
					id
					accessType
					user {
						id
						email
						image
						fullName
						lastName
						firstName
						isActive
						isConfigured
						hasReceivedInvites
						isGuest
					}
				}
				pendingStreamInvites {
					id
					email
					isExpired
				}

				notes(first: 3) {
					edges {
						node {
							id
							noteuserSet {
								id
								user {
									__typename
									id
									email
									firstName
									fullName
									image
									isActive
									isConfigured
									isGuest
								}
							}
						}
					}
				}
			}
		}
	}
`;

interface Props {
	streamId: string;
	showSharingPaywall?: boolean;
	prepopulateDeferredExternals: boolean;
	hideGeneralAccess: boolean;
	deferredExternals: readonly { id: string; email: string; isExternal?: boolean }[];
	allowExternalInviting: boolean | null;
	allowGuestInviting: boolean | null;
	allowInternalInviting: boolean;
	sharedNoteStreamUsers: MainStream["notestreamusers"];
	pendingStreamInvites: NonNullable<ShareModalQuery$data["noteStream"]>["pendingStreamInvites"];
	onShared?(newUserIds: string[], newEmails: string[]): void;
	readOnly: boolean;
	emailDomainList: readonly string[];
}

export function ShareSection(props: Props) {
	const {
		streamId,
		showSharingPaywall,
		prepopulateDeferredExternals,
		hideGeneralAccess,
		deferredExternals,
		allowExternalInviting,
		allowGuestInviting,
		allowInternalInviting,
		sharedNoteStreamUsers,
		pendingStreamInvites,
		onShared,
		readOnly,
		emailDomainList,
	} = props;

	const invitesAllowed = allowInternalInviting || allowExternalInviting || allowGuestInviting;

	const [users, setUsers] = useState<readonly UserMultiSelectorOption[]>([]);
	const [contacts, setContacts] = useState<readonly Contact[]>(() => {
		if (prepopulateDeferredExternals) {
			// The following rules are based off of which emails (internal/external domain) you're allowed to add from the share modal
			if (allowExternalInviting) {
				return deferredExternals.slice(0, 20);
			}

			const internalEmails = deferredExternals.filter(deferredExternal =>
				emailDomainList.some(domain => deferredExternal.email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)),
			);
			const externalEmails = deferredExternals.filter(
				deferredExternal =>
					!emailDomainList.some(domain => deferredExternal.email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)),
			);

			let tmpContacts = [];
			if (allowInternalInviting) {
				tmpContacts.push(...internalEmails);
			}
			if (allowGuestInviting) {
				tmpContacts.push(...externalEmails);
			}
			return tmpContacts.slice(0, 20);
		}
		return [];
	});

	// There's a race condition caused by useFeature() in ShareModal/index.tsx that causes the
	// ShareSection to be blank when opened 2 or more times.
	// Internally useFeature uses a promise and then sets state once the promise resolves,
	// but the initial return value is undefined. My theory is that the first time the share modal is opened
	// this race condition doesn't occur because the promise resolves before the relay query is fulfilled.
	// Then on subsequent opens the query is cached so the undefined value is used before the promise resolves
	// which results in the allowExternalInviting prop to be false when passed to ShareModal. Since that prop is
	// used here to determine the initial contacts state, the inital value is incorrect. When the promise resolves
	// and the props are updated this component doesn't update state on an update. Therefore we use the useEffect hook
	// to update the state when the allowExternalInviting prop changes.
	useEffect(() => {
		if (allowExternalInviting && prepopulateDeferredExternals) {
			setContacts(deferredExternals.slice(0, 20));
		}
	}, [allowExternalInviting, prepopulateDeferredExternals, deferredExternals]);

	const [addedDeferredInvites, setAddedDeferredInvites] = useState(false);
	const hasExternalContact = useMemo(() => {
		return contacts.some(contact => contact.isExternal);
	}, [contacts]);
	const hasNewInternalContact = useMemo(() => {
		return contacts.some(contact => !contact.isExternal && !contact.hasConfiguredUser);
	}, [contacts]);

	const sendingInvitation = contacts.some(contact => !contact.hasConfiguredUser);
	const showSharingFeatureModal = useCallback(() => showFeatureModal("sharing"), []);

	const onContactsChange = useCallback((val: MultiValue<Contact>) => {
		setContacts(val);
	}, []);

	const addDeferredInvites = useCallback(() => {
		setAddedDeferredInvites(true);
		onContactsChange(deferredExternals.concat(contacts) || []);
	}, [contacts, deferredExternals, onContactsChange]);

	const onShare = useCallback(async () => {
		if (readOnly) return;
		const captchaToken = await recaptcha("user_dialogues");
		const emails = invitesAllowed
			? contacts.map(contact => contact.email)
			: users.map(user => user.email || user.avatar?.email || "");

		commitMutation<ShareMutationType>(environment, {
			mutation: shareStreamMutation,
			variables: {
				id: streamId,
				emails,
				captchaToken,
			},
			onCompleted: ({ noteStreamShare }, errors) => {
				setUsers([]);
				setContacts([]);

				if (errors && errors.length) {
					const errorMessage = getFirstUserFacingError(errors) ?? errors[0].message;
					toast.error(errorMessage);
					return;
				}
				if (!noteStreamShare) {
					toast.error("Unable to share this series");
					return;
				}

				const sharedUserIds = sharedNoteStreamUsers.map(nsu => nsu.user.id);

				/* diff between these two list: old shared users vs new shared users */
				const newUserIds = noteStreamShare.noteStream.notestreamusers
					.map(o => o.user.id)
					.filter(id => !sharedUserIds.includes(id));
				const newEmails = noteStreamShare.noteStream.pendingStreamInvites
					?.filter(invite => !pendingStreamInvites?.some(existingInvite => existingInvite.email === invite.email))
					.map(invite => invite.email);
				onShared?.(newUserIds, newEmails || []);
			},
		});
	}, [users, contacts, streamId, invitesAllowed, onShared, sharedNoteStreamUsers, readOnly, pendingStreamInvites]);

	const onUsersChange = useCallback(
		(newUsers: MultiValueType<UserMultiSelectorOption>) => {
			setUsers([...newUsers]);
		},
		[setUsers],
	);

	const sharingInput = useMemo(() => {
		return invitesAllowed ? (
			<EmailListInput
				isMulti
				autoFocus
				useNewTeammateBtn
				key="email-list-input"
				internalInvitesAllowed={allowInternalInviting}
				guestInvitesAllowed={allowGuestInviting}
				externalInvitesAllowed={allowExternalInviting}
				value={contacts}
				exclude={sharedNoteStreamUsers
					.filter(noteStreamUser => noteStreamUser.accessType !== "partial" && noteStreamUser.user.isActive)
					.map(a => a.user.email)
					.concat(pendingStreamInvites ? pendingStreamInvites.map(a => a.email) : [])}
				placeholder="Add one or more teammates by name or email."
				onChange={onContactsChange}
				className={cx(styles.emailListInput, { [styles.empty]: contacts.length === 0 })}
			/>
		) : (
			<UserMultiSelector
				selectedUsers={users}
				ranked
				textOnly
				autoFocus
				defaultOptions
				placeholder="Add one or more teammates by name or email."
				excludeUserIDs={sharedNoteStreamUsers
					.filter(noteStreamUser => noteStreamUser.accessType !== "partial")
					.map(a => a.user.id)}
				onChange={onUsersChange}
				className={styles.userSelect}
			/>
		);
	}, [
		users,
		contacts,
		allowExternalInviting,
		allowGuestInviting,
		allowInternalInviting,
		invitesAllowed,
		onContactsChange,
		onUsersChange,
		sharedNoteStreamUsers,
		pendingStreamInvites,
	]);

	return (
		<>
			<div style={{ display: "flex" }}>
				<h6 className={styles.subtitle} style={{ flexGrow: 1 }}>
					{prepopulateDeferredExternals && hideGeneralAccess
						? "Share with external users"
						: "Share with teammates and external users"}
				</h6>
				{allowExternalInviting &&
					!addedDeferredInvites &&
					!prepopulateDeferredExternals &&
					deferredExternals?.length > 0 && (
						<ButtonLink onClick={addDeferredInvites} className={styles.addDeferredGuests}>
							{deferredExternals.length > 1 ? (
								<>Add {deferredExternals.length} external users</>
							) : (
								<>Add 1 external user</>
							)}
						</ButtonLink>
					)}
			</div>
			<div className={styles.shareInputs} data-test-id="share-modal-share-inputs">
				{sharingInput}
				{(users.length > 0 || contacts.length > 0) && (
					<div className={styles.shareInputButtons}>
						<Button
							variant={showSharingPaywall ? "billing" : "primary"}
							style={{ marginLeft: "4px" }}
							disabled={readOnly && !showSharingPaywall}
							onClick={showSharingPaywall ? showSharingFeatureModal : onShare}
							data-test-id="share-modal-share-button"
						>
							Share {showSharingPaywall && <FontAwesomeIcon icon={billingUpgradeIconDef} />}
						</Button>
					</div>
				)}
			</div>
			{sendingInvitation && hasExternalContact && (
				<div className={styles.invitationWarning}>
					<p>
						{hideGeneralAccess
							? "External users will be invited to join Fellow."
							: "New teammates and external users will be invited to join Fellow."}
					</p>
				</div>
			)}
			{sendingInvitation && hasNewInternalContact && (
				<div className={styles.invitationWarning}>
					<p>New teammates will be invited to join Fellow.</p>
				</div>
			)}
		</>
	);
}
